export interface SetupGuideTroubleshootingItem {
    error: string;
    description: string;
    descriptionHighlights?: string[];
}

export type SetupGuideTroubleshootingProvider = "databricks" | "thoughtspot" | "snowflake";

export const DATABRICKS_TROUBLESHOOTING_ITEMS: SetupGuideTroubleshootingItem[] = [
    {
        error: "workspace_url or account_url is required",
        description: "Enter the full https:// workspace URL.",
    },
    {
        error: "Databricks authentication failed",
        description:
            "Confirm Application ID and secret; ensure Account ID is set for service principal auth.",
    },
    {
        error: "user_group is required",
        description:
            "Enter the Databricks group name; create one in Settings → Identity and access → Groups.",
    },
    {
        error: "Warehouse not found",
        description: "Copy the ID from SQL Warehouses → Connection details → HTTP Path.",
    },
    {
        error: "Principal lacks warehouse access",
        description: "Add Can use on the warehouse via the kebab menu → Permissions.",
        descriptionHighlights: ["Can use"],
    },
    {
        error: "Catalog not found",
        description: "Verify the catalog name in Catalog Explorer; re-run GRANT USE CATALOG",
        descriptionHighlights: ["GRANT", "USE CATALOG"],
    },
    {
        error: "Lakebase endpoint not found",
        description: "Confirm project and branch names in Databricks → Lakebase.",
    },
    {
        error: "password authentication failed (Lakebase)",
        description:
            "The Postgres OAuth role is missing — run databricks_create_role(…) and grant permissions.",
    },
];

export const THOUGHTSPOT_TROUBLESHOOTING_ITEMS: SetupGuideTroubleshootingItem[] = [
    {
        error: "ThoughtSpot connection config is missing required fields",
        description:
            "Confirm Connection name, Cluster URL, User name, and Secret Key are all filled in.",
    },
    {
        error: "ThoughtSpot authentication failed",
        description:
            "Re-check the secret key under Develop → Customizations → Security settings; verify the correct org is selected.",
    },
    {
        error: "ThoughtSpot authentication timed out",
        description:
            "Confirm the Cluster URL has no trailing slash; check firewall or VPN rules.",
    },
    {
        error: "Unable to connect to ThoughtSpot",
        description:
            "Confirm the cluster URL is correct and reachable from Blitz's network.",
    },
    {
        error: "Token generation returned an empty token",
        description:
            "Re-enable Trusted Authentication in Develop → Customizations → Security settings and copy the current key.",
    },
    {
        error: "Unable to resolve ThoughtSpot connection GUID",
        description:
            "Open Data → Connections and copy the name character-for-character — the match is case-sensitive.",
    },
    {
        error: "Connection name already exists",
        description: "Choose a different name in the Connection Name field.",
    },
    {
        error: "Same credentials already exist",
        description:
            "Each ThoughtSpot connection must use a distinct cluster URL + username combination.",
    },
];

export const SNOWFLAKE_TROUBLESHOOTING_ITEMS: SetupGuideTroubleshootingItem[] = [
    {
        error: "Snowflake authentication failed",
        description:
            "Confirm the Account ID is in ORG-ACCOUNT format with no .snowflakecomputing.com; re-enter credentials.",
        descriptionHighlights: ["ORG-ACCOUNT", ".snowflakecomputing.com"],
    },
    {
        error: "Snowflake validation failed for warehouse/database/schema",
        description: "Verify exact names from Step 4; re-run the GRANT statements in Step 3.",
        descriptionHighlights: ["Step 4", "GRANT", "Step 3"],
    },
    {
        error: "Login timeout",
        description: "Ensure Blitz's IPs are not blocked by your Snowflake network policy.",
    },
    {
        error: "Role is not available",
        description:
            "Admin → Users & Roles → Users → edit user → Advanced User Options → set Default Role to BLITZ_ROLE.",
        descriptionHighlights: ["BLITZ_ROLE"],
    },
    {
        error: "Password authentication is not allowed",
        description:
            "The account enforces MFA or disables passwords — use a password-capable integration user, or move to key-pair/OAuth.",
        descriptionHighlights: ["password-capable", "key-pair/OAuth"],
    },
    {
        error: "Insufficient privileges to create SEMANTIC VIEW",
        description:
            "Run GRANT CREATE SEMANTIC VIEW ON SCHEMA ... TO ROLE blitz_role (Step 3f).",
        descriptionHighlights: ["GRANT CREATE SEMANTIC VIEW", "Step 3f"],
    },
    {
        error: "Insufficient privileges to create AGENT",
        description: "Run GRANT CREATE AGENT ON SCHEMA ... TO ROLE blitz_role (Step 3f).",
        descriptionHighlights: ["GRANT CREATE AGENT", "Step 3f"],
    },
    {
        error: "Cortex Analyst not available",
        description:
            "Run ALTER ACCOUNT SET ENABLE_CORTEX_ANALYST = TRUE as ACCOUNTADMIN; check Snowflake's regional availability matrix.",
        descriptionHighlights: ["ALTER ACCOUNT SET ENABLE_CORTEX_ANALYST = TRUE", "ACCOUNTADMIN"],
    },
];

export const TROUBLESHOOTING_BY_PROVIDER: Record<
    SetupGuideTroubleshootingProvider,
    SetupGuideTroubleshootingItem[]
> = {
    databricks: DATABRICKS_TROUBLESHOOTING_ITEMS,
    thoughtspot: THOUGHTSPOT_TROUBLESHOOTING_ITEMS,
    snowflake: SNOWFLAKE_TROUBLESHOOTING_ITEMS,
};
